import objectSchema from './item.graphql';
import { ref, watch } from '@vue/composition-api';
import { nanoid } from 'nanoid';
import cloneDeep from 'lodash.clonedeep';
import useEntity from '../_basic';
import useObjects from './index';
import useGroups from '../users/groups';
import sortBy from 'lodash.sortby';
import useDatasets from '@/api/datasets';
import useDevices from '@/api/devices';
import useNoty from '@/api/notifications';
import useMedias from '@/api/medias';
import useMedia from '@/compositions/useMedia';
import useApps from '@/api/apps';
import download from '@/utils/download';
import convertBytes from '@/utils/convertBytes';
import convertDate from '@/utils/convertDate';
import getIconByTags from '@/utils/getIconByTags';
import checkNull from '@/utils/checkNull';
import { useLazyQuery, useResult } from '@vue/apollo-composable';
import useObjectActions from '@/api/objects/useObjectActions';
import useCommonModal from '@/compositions/useCommonModal';
import usePropertyActions from '@/api/objects/usePropertyActions';
import hideIfEmpty from '@/utils/hideIfEmpty';
import { Wormhole } from 'portal-vue';

export default (props, context) => {
  const entityComposition = useEntity(props, context);
  const modalComposition = useCommonModal();
  const { groups } = useGroups;
  const { object } = useObjects();
  const objectApi = useObjects();
  const { devices } = useDevices();
  const { datasets } = useDatasets();
  const { apps } = useApps();
  const { medias } = useMedias();
  const { getImageById, isImage, computeIcon } = useMedia();
  const { notifications } = useNoty();
  const entity = ref(null);
  const objectType = ref('');
  const nestedId = ref('');
  const downloadLink = ref('');
  const entityTitle = ref('');
  const isMediaTypeExist = ref(false);
  const objectNotifications = ref([]);
  const controlExecutions = ref([]);
  const objectItem = ref(null);
  const isAlreadySubscribed = ref(false);
  const nestedComponent = ref(null);

  const mainVariables = ref({
    id: props.id
  });
  const lazyQuery = useLazyQuery(objectSchema.load, mainVariables, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only'
  });

  const loadObjectName = useLazyQuery(
    objectSchema.loadObjectName,
    {
      id: context.root.$route.params.id
    },
    {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only'
    }
  );

  const objectParentName = useResult(loadObjectName.result, '', data => {
    return data.object.name;
  });

  const { getActions } = useObjectActions(props, context, lazyQuery);
  const propertyActions = usePropertyActions(props, context, lazyQuery);

  const blinkItemInTable = id => {
    context.root.$nextTick(() => {
      const el = document.querySelector(`[data-id-row="${id}"]`);
      if (el) {
        let nearestTr = el.closest('tr');
        if (nearestTr) {
          el.closest('tr').scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
          });
          el.closest('tr').classList.add('blink-row');
          setTimeout(() => {
            el.closest('tr').classList.remove('blink-row');
          }, 2000);
        }
      }
    });
  };

  const recomputeData = async () => {
    let object = lazyQuery.result.value.object;
    let id = lazyQuery.result.value.object.id;
    let isNested = Boolean(context.root.$route.query.nestedId);

    if (isNested) {
      loadObjectName.load();
    }

    objectItem.value = {
      data: {
        object: {
          ...lazyQuery.result.value.object,
          objectParentName: objectParentName.value
        }
      }
    };

    const entityLocal = entity;

    notifications.setParams({
      orderBy: 'CREATED_AT_DESC',
      filter: {
        objectId: {
          equalTo: id
        }
      }
    });

    objectNotifications.value = await notifications.load(id).catch(e => []);

    objectType.value = object.schemaType;

    isMediaTypeExist.value = object.mediaType;

    const buildNotifications = notifications => {
      const list = [];

      notifications.forEach(item => {
        const preFilled = {
          raw: item.node,
          id: item.node.id,
          title: item.node.message,
          subtitles: [convertDate(item.node.createdAt)],
          isHoverAction: false,
          contextMenu: [],
          actionClickElement: () => {
            import('@/router/routes/notifications/item/index.vue').then(r => {
              const passengers = [
                context.root.$createElement(r.default, {
                  props: {
                    id: item.node.id,
                    isNestedEntity: true
                  }
                })
              ];
              Wormhole.open({
                to: 'nested',
                from: 'object',
                passengers
              });
            });
          },
          icon: getIconByTags(item.node.tags),
          action: 'select'
        };

        list.push(preFilled);
      });

      return list;
    };

    const humanReadableFlag = (nonHuman, object) => {
      let schema = object?.data?.object?.schema || object?.schema;
      if (!schema) {
        return nonHuman;
      } else {
        const associateProperty = schema.schemaProperties.filter(
          item => item.property === nonHuman
        )[0];
        if (associateProperty && associateProperty.description) {
          return associateProperty.description;
        }
        return nonHuman;
      }
    };

    const humanReadableValue = propertyItem => {
      let schema = object?.data?.object?.schema || object?.schema;
      if (!schema) {
        return false;
      } else {
        let schemaOfProperty = schema.schemaProperties.find(
          item => item.property === propertyItem.property
        );

        let valueSet = schemaOfProperty?.valueSet;

        if (valueSet) {
          let list = [];

          for (let prop in valueSet) {
            list.push({ value: prop, key: valueSet[prop] });
          }

          let target = list.find(item => item.key === propertyItem.value);
          if (target?.value === undefined) return null;
          return target.value;
        } else {
          return false;
        }
      }
    };

    const createTags = tags => {
      let tagsInner = [];
      if (tags && Array.isArray(tags)) {
        tags.forEach(item => {
          tagsInner.push({
            id: nanoid(),
            title: item
          });
        });
      }
      if (tagsInner.length) {
        return {
          id: nanoid(),
          subheader: 'Tags',
          contextMenu: [],
          properties: {
            chips: tagsInner
          }
        };
      }
      return null;
    };

    const groupProperties = objectEntity => {
      const list = [];
      const groupNames = objectEntity.data.object.objectProperties.map(
        item => item.groupName
      );
      const unicGroupName = new Set(groupNames);

      const createPropertiesGroups = () => {
        unicGroupName.forEach(item => {
          let allInGroup = [];

          objectEntity.data.object.objectProperties.forEach(itemNested => {
            let linkedProperty = ref(null);

            if (itemNested.linkedPropertyId) {
              objectApi.object
                .getObjectProperty(itemNested.linkedPropertyId)
                .then(res => {
                  linkedProperty.value = res.data.objectProperty;
                });
            }

            if (itemNested.groupName === item) {
              let isObject = typeof itemNested.value === 'object';
              let isSize = itemNested.property === 'SIZE';

              const getValue = value => {
                if (value === null) return 'n/a';
                if (isObject) return JSON.stringify(value);
                if (isSize) return convertBytes(value);
                return value;
              };

              let value = getValue(itemNested.value);
              let humanReadable = humanReadableValue(itemNested);

              allInGroup.push({
                id: itemNested.id,
                rawItem: itemNested,
                contextMenu: propertyActions.getActions({
                  ...itemNested,
                  title: humanReadableFlag(itemNested.property, objectEntity)
                }),
                schemaItems: objectEntity.data.object.schema.schemaProperties,
                date: '',
                raw: objectEntity.data.object.objectProperties,
                title: `${humanReadableFlag(
                  itemNested.property,
                  objectEntity
                )}: ${humanReadable || value}`,
                subtitle: `${
                  itemNested.updatedAt
                    ? convertDate(itemNested.updatedAt)
                    : 'n/a'
                } by ${checkNull(itemNested?.userByBy?.login)}`,
                icon: 'none',
                linkedPropertyId: linkedProperty,
                ownerChange: ``,
                isHoverAction: true
              });
            }
          });

          allInGroup = sortBy(allInGroup, 'title');
          const preInterface = {
            id: nanoid(),
            subheader: item,
            properties: {
              ...allInGroup
            }
          };

          list.push(preInterface);
        });
      };

      createPropertiesGroups();

      return sortBy(list, 'subheader');
    };

    const objectsList = (object, type) => {
      const list = [];

      object.objectsToObjectsByObject1Id
        .filter(item => !item.forced)
        .filter(item => {
          if (type === 'media') {
            return item.object2.schemaType === 'media';
          } else {
            return ['device', 'dataset', 'application'].includes(
              item.object2.schemaType
            );
          }
        })
        .forEach(item => {
          const preFilled = {
            raw: item.object2,
            id: item.object2.id,
            connectionId: item.id,
            title: item.object2.name,
            isHoverAction: true,
            contextMenu: getActions({
              ...item.object2,
              connectionId: item.id
            }),
            actionClickElement: () => {
              // if (!isNested) {
              //   context.root.$router.push({
              //     query: {
              //       ...context.root.$route.query,
              //       nestedId: item.object2.id
              //     }
              //   });
              // }
            },
            avatar: computeIcon(item.object2.schema),
            icon: {
              name: '',
              color: '#fff',
              bg: '#333'
            },
            action: 'select'
          };
          switch (item.object2.schemaType) {
            case 'media':
              preFilled.icon.name = 'mdi-image';
              break;
            case 'dataset':
              preFilled.icon.name = 'mdi-puzzle';
              break;
            case 'application':
              preFilled.icon.name = 'mdi-application-cog';
              break;
            default:
              preFilled.icon.name = 'mdi-memory';
              break;
          }

          list.push(preFilled);
        });

      return sortBy(list, 'title');
    };

    const fragmentsList = object => {
      const list = [];

      if (!isNested) {
        object.objectsToObjectsByObject1Id
          .filter(item => item.forced)
          .forEach(item => {
            const preFilled = {
              raw: item.object2,
              id: item.object2.id,
              connectionId: item.id,
              title: item.object2.name,
              isHoverAction: true,
              contextMenu: getActions(item.object2),
              actionClickElement: () => {
                context.root.$router.push({
                  query: {
                    nestedId: item.object2.id
                  },
                  meta: {}
                });
              },
              icon: {
                name: '',
                color: '#fff',
                bg: '#333'
              },
              action: 'select'
            };
            switch (item.object2.schemaType) {
              case 'media':
                preFilled.icon.name = 'mdi-image';
                break;
              case 'dataset':
                preFilled.icon.name = 'mdi-puzzle';
                break;
              case 'application':
                preFilled.icon.name = 'mdi-application-cog';
                break;
              default:
                preFilled.icon.name = 'mdi-memory';
                break;
            }

            list.push(preFilled);
          });
      }

      return sortBy(list, 'title');
    };

    const linksList = object => {
      const list = [];

      if (!isNested) {
        object.linksObjects.forEach(item => {
          const preFilled = {
            raw: item.object1,
            id: item.object1.id,
            connectionId: item.id,
            title: item.object1.name,
            isHoverAction: true,
            contextMenu: getActions(item.object1),
            actionClickElement: () => {
              context.root.$router.push({
                query: {
                  nestedId: item.object1.id
                },
                meta: {}
              });
            },
            icon: {
              name: '',
              color: '#fff',
              bg: '#333'
            },
            action: 'select'
          };
          switch (item.object1.schemaType) {
            case 'media':
              preFilled.icon.name = 'mdi-image';
              break;
            case 'dataset':
              preFilled.icon.name = 'mdi-puzzle';
              break;
            case 'application':
              preFilled.icon.name = 'mdi-application-cog';
              break;
            default:
              preFilled.icon.name = 'mdi-memory';
              break;
          }

          list.push(preFilled);
        });
      }

      return sortBy(list, 'title');
    };

    const createTabs = () => {
      return [
        {
          id: 'general',
          title: 'general',
          type: 'scheduleGeneral'
        },
        {
          id: 'objects',
          title: 'objects',
          type: 'list'
        },
        ...hideIfEmpty(groupProperties(objectItem.value).length, [
          {
            id: 'properties',
            title: 'properties',
            type: 'properties'
          }
        ]),
        ...hideIfEmpty(
          buildNotifications(
            objectNotifications.value.notificationsConnection.edges
          ).length,
          [
            {
              id: 'notifications',
              title: 'notifications',
              type: 'list'
            }
          ]
        )
      ];
    };
    entityTitle.value = object.name;

    entityLocal.value = {
      logs: {
        items: [],
        additionalItems: []
      },
      entity: object,
      image: '',
      imageFallback: '',
      isSvg: false,
      type: object.schemaType,
      enabled: object.enabled,
      batterLevel: object.deviceBatteryLevel,
      deviceBatteryLevel: object.deviceBatteryLevel,
      batteryLow: object.deviceIsBatteryLow,
      deviceIsBatteryLow: object.deviceIsBatteryLow,
      deviceBatteryType: object.deviceBatteryType,
      deviceResponseStatus: object.deviceResponseStatus,
      deviceEmulation: object.deviceEmulation,
      mediaType: object.mediaType,
      mediaSize: convertBytes(object.mediaSize),
      muted: object.muted,
      favourite: object.favourite,
      id: object.id,
      title: object.name,
      schemaType: object.schemaType,
      objectParentName: object.objectParentName,
      contextMenu: getActions(object),
      tabsMeta: createTabs(),
      tabs: [
        {
          items: [
            ...hideIfEmpty(
              object.objectProperties.find(item => item.key === 'settingsTags')
                ?.value,
              [
                createTags(
                  object.objectProperties.find(
                    item => item.key === 'settingsTags'
                  ).value
                )
              ]
            ),
            ...hideIfEmpty(
              object.objectProperties.find(
                item => item.key === 'settingsMessage'
              )?.value,
              [
                {
                  id: nanoid(),
                  subheader: 'Message',
                  contextMenu: [],
                  properties: [
                    {
                      id: nanoid(),
                      isHoverAction: false,
                      icon: 'none',
                      title: object.objectProperties.find(
                        item => item.key === 'settingsMessage'
                      )?.value
                    }
                  ]
                }
              ]
            ),
            {
              id: nanoid(),
              subheader: 'General info',
              contextMenu: [],
              properties: [
                {
                  id: nanoid(),
                  title: object.id,
                  icon: {
                    name: 'mdi-identifier'
                  },
                  isHoverAction: false
                }
              ]
            },
            {
              id: nanoid(),
              subheader: 'Access',
              contextMenu: [
                {
                  id: 'edit-rights',
                  text: 'Edit',
                  icon: 'mdi-pencil',
                  _enabled: true
                },
                {
                  id: 'copy',
                  text: 'Copy',
                  icon: 'mdi-image-filter-none',
                  _enabled: true
                }
              ],
              properties: [
                {
                  id: nanoid(),
                  raw: {
                    ...object.userGroupByEditorgroup,
                    type: 'editorgroup'
                  },
                  title: `Editors: ${object.userGroupByEditorgroup?.groupName || null}`,
                  subtitle: '',
                  icon: {
                    name: 'mdi-pencil'
                  },
                  dropdown: [],
                  isHoverAction: true
                },
                {
                  id: nanoid(),
                  raw: {
                    ...object.userGroupByUsergroup,
                    type: 'usergroup'
                  },
                  title: `Users: ${object.userGroupByUsergroup?.groupName || null}`,
                  subtitle: '',
                  icon: {
                    name: 'mdi-play-circle-outline'
                  },
                  dropdown: [],
                  isHoverAction: true
                },
                {
                  id: nanoid(),
                  raw: {
                    ...object.userGroupByReadergroup,
                    type: 'readergroup'
                  },
                  title: `Readers: ${checkNull(object.userGroupByReadergroup?.groupName)}`,
                  subtitle: '',
                  icon: {
                    name: 'mdi-eye'
                  },
                  dropdown: [],
                  isHoverAction: true
                }
              ]
            },
            ...hideIfEmpty(object.description, [
              {
                id: nanoid(),
                subheader: 'Description',
                contextMenu: [],
                properties: [
                  {
                    id: nanoid(),
                    title: checkNull(object?.description),
                    icon: 'none',
                    isHoverAction: false
                  }
                ]
              }
            ])
          ],
          additionalItems: []
        },
        {
          items: [...objectsList(object)],
          additionalItems: [
            {
              id: 'add-object',
              name: 'add-object',
              title: 'Link existing object',
              handleAction: connectObject,
              icon: entityComposition.createIcon('mdi-plus')
            },
            {
              id: 'add-new-object',
              name: 'add-new-object',
              title: 'Add new object',
              handleAction: createNew,
              icon: entityComposition.createIcon('mdi-plus')
            }
          ]
        },
        ...hideIfEmpty(groupProperties(objectItem.value).length, [
          {
            items: [...groupProperties(objectItem.value)],
            additionalItems: []
          }
        ]),
        ...hideIfEmpty(
          buildNotifications(
            objectNotifications.value.notificationsConnection.edges
          ).length,
          [
            {
              items: buildNotifications(
                objectNotifications.value.notificationsConnection.edges
              ),
              additionalItems:
                objectNotifications.value.notificationsConnection.totalCount >
                10
                  ? [
                      {
                        id: 'show-noty-by-object',
                        name: 'show-noty-by-object',
                        title: 'Show all',
                        icon: entityComposition.createIcon('mdi-arrow-down')
                      }
                    ]
                  : []
            }
          ]
        )
      ]
    };

    entityLocal.value.favourite = object.favourite;
    entityLocal.value.enabled = object.enabled;

    const getPicture = fallback => {
      entityLocal.value.imageFallback = fallback;
      if (object.schema.mPicture) {
        return getImageById(object.schema.mPicture);
      } else {
        return fallback;
      }
    };

    entityLocal.value.image = getPicture('/img/schedule.svg');
  };

  const getRights = () => {
    let propName = entityComposition.checkRights();

    if (propName === 'editorgroup') {
      return entityComposition.dropdownRights.value.map(item => ({
        ...item,
        disabled: item.name === 'Nobody'
      }));
    }
    return entityComposition.dropdownRights.value.map(item => ({
      ...item,
      disabled: false
    }));
  };
  const changeRights = () => {
    entityComposition.showPopup.value = false;
    let propName = entityComposition.checkRights();
    if (propName) {
      object
        .update(objectItem.value.data.object.id, {
          [propName]: entityComposition.popupValue.value.id
        })
        .then(() => {
          // loadData(objectItem.value.data.object.id);
        })
        .finally(() => {
          entityComposition.handleClose();
        });
    }
  };

  const changeGroupRights = data => {
    entityComposition.popupLoading.value = true;
    if (data) {
      object
        .update(objectItem.value.data.object.id, data)
        .then(() => {
          // loadData(objectItem.value.data.object.id);
        })
        .catch(() => ({}))
        .finally(() => {
          entityComposition.popupLoading.value = false;
          object.loading.value = false;
          entityComposition.handleClose();
        });
    }
  };

  const updateTable = isCloseCard => {
    switch (objectType.value) {
      case 'media':
        if (isCloseCard) context.root.$router.push({ name: 'medias' });
        return medias.load();
      case 'dataset':
        if (isCloseCard) context.root.$router.push({ name: 'datasets' });
        return datasets.load();
      case 'application':
        if (isCloseCard) context.root.$router.push({ name: 'apps' });
        return apps.load();
      default:
        if (isCloseCard) context.root.$router.push({ name: 'devices' });
        return devices.load();
    }
  };

  const connectObject = filterBy => {
    modalComposition.modal.show({
      component: () => import('@/components/app-entity/modals/AppDialog.vue'),
      bind: {
        nameModal: 'LinkObject'
      },
      slots: {
        default: {
          component: () =>
            import('@/components/app-entity/modals/LinkObject.vue'),
          bind: {
            targetToLink: lazyQuery.result.value.object.id,
            typeConnection: 'objectToObjects',
            filterBy: filterBy
          },
          on: {
            close() {
              modalComposition.modal.hide('LinkObject');
            }
          }
        }
      }
    });
  };

  const createNew = () => {
    let component = import('@/components/app-entity/modals/CreateObject');

    modalComposition.modal.show({
      component: () => import('@/components/app-entity/modals/AppDialog.vue'),
      bind: {
        nameModal: 'EditObject'
      },
      slots: {
        default: {
          component: () => component,
          bind: {
            entity: {},
            linkAfterCreation: true
          },
          on: {
            close() {
              modalComposition.modal.hide('EditObject');
            }
          }
        }
      }
    });
  };

  const createMedia = () => {
    let component = import('@/components/app-entity/modals/CreateMedia');

    modalComposition.modal.show({
      component: () => import('@/components/app-entity/modals/AppDialog.vue'),
      bind: {
        nameModal: 'MediaObject'
      },
      slots: {
        default: {
          component: () => component,
          bind: {
            entity: {},
            linkAfterCreation: true
          },
          on: {
            close() {
              modalComposition.modal.hide('MediaObject');
            }
          }
        }
      }
    });
  };

  const executeControl = e => {
    entityComposition.popupLoading.value = true;
    object
      .createControlsExecution({
        controlExecution: {
          name: e.name,
          objectId:
            context.root.$route.query?.nestedId ||
            context.root.$route.params.id,
          params: e.params
        }
      })
      .then(() => {
        entityComposition.handleClose();
        object.loading.value = false;
        // return loadData(entity.value.id);
      })
      .finally(() => {
        entityComposition.popupLoading.value = false;
        entityComposition.handleClose();
      });
  };

  const toggleFavorite = flag => {
    object
      .addToFavorite({
        objectsId: [context.root.$route.params.id],
        favourite: !flag
      })
      // .then(() => {
      //   return updateTable();
      // })
      .then(() => {
        blinkItemInTable(context.root.$route.params.id);
      });
  };

  const closeHistory = () => {
    entityComposition.currentShow.value = 'entity';
    entityComposition.currentContext.value = null;
  };

  const subscribeVariables = ref(null);

  lazyQuery.onResult(({ data }) => {
    subscribeVariables.value = {
      filterA: {
        id: [
          props.id,
          ...data.object.objectsToObjectsByObject1Id.map(
            item => item.object2.id
          )
        ]
      },
      id: props.id
    };
    if (!isAlreadySubscribed.value) {
      subscribeToUpdate();
    }
  });

  const linkedIds = useResult(lazyQuery.result, null, data => {
    return data.object.objectsToObjectsByObject1Id.map(item => item.object2.id);
  });
  const subscribeToUpdate = () => {
    if (!subscribeVariables.value) return false;
    lazyQuery.subscribeToMore({
      document: objectSchema.subscribeObject,
      variables: subscribeVariables,
      updateQuery: (previousResult, { subscriptionData }) => {
        let clone = cloneDeep(previousResult);
        const relatedNode = subscriptionData.data?.Objects?.relatedNode;
        const eventType = subscriptionData.data?.Objects?.event;

        if (eventType === 'insert') {
          clone.object.objectsToObjectsByObject1Id.push(relatedNode.object2);
          return clone;
        }
        updateTable();
      }
    });
    isAlreadySubscribed.value = true;
  };

  watch(
    () => context.root.$route.query.nestedId,
    value => {
      closeHistory();
      mainVariables.value.id = value || props.id;
      subscribeVariables.value = {
        filterA: {
          id: [value || props.id, ...linkedIds.value]
        },
        id: value || props.id
      };
    }
  );

  watch(
    () => props.id,
    value => {
      closeHistory();
      mainVariables.value.id = value;

      subscribeVariables.value = {
        filterA: {
          id: [props.id, ...linkedIds.value]
        },
        id: props.id
      };
    }
  );

  watch(
    () => lazyQuery.result.value,
    async value => {
      if (value) {
        await recomputeData();
      }
    }
  );

  watch(
    () => context.root.$route.query.t,
    async value => {
      if (value) {
        await recomputeData();
        context.root.$router.replace({
          query: null
        });
      }
    }
  );

  watch(
    () => entityComposition.typeAction.value,
    value => {
      switch (value) {
        case 'edit-rights':
          groups.load().then(res => {
            entityComposition.dropdownRights.value = res;

            if (entityComposition.currentContext.value.raw) {
              res.forEach(item => {
                if (item.id === entityComposition.currentContext.value.raw.id) {
                  entityComposition.popupValue.value = item;
                }
              });
            }
          });
          break;
        case 'enable-disable':
          entityComposition.typeAction.value = '';
          object
            .update(entityComposition.entityId.value, {
              // enabled: !isEnabled(entityComposition.entityId.value)
            })
            // .then(() => {
            //   return updateTable();
            // })
            .then(() => {
              blinkItemInTable(entityComposition.entityId.value);
            });
          break;
        case 'mute-unmute':
          entityComposition.typeAction.value = '';
          object
            .muteObject({
              objectsId: [entityComposition.entityId.value]
              // muted: !isMuted(entityComposition.entityId.value)
            })
            // .then(() => {
            //   return updateTable();
            // })
            .then(() => {
              blinkItemInTable(entityComposition.entityId.value);
            });
          break;
        case 'download-file':
          entityComposition.typeAction.value = '';
          if (isMediaTypeExist.value.length > 0) {
            download(downloadLink.value, entityTitle.value);
          }
          break;
      }
    }
  );

  return {
    ...entityComposition,
    entity,
    objectItem,
    object,
    loading: lazyQuery.loading,
    changeRights,
    getRights,
    connectObject,
    executeControl,
    closeHistory,
    toggleFavorite,
    changeGroupRights,
    load: lazyQuery.load,
    subscribeToUpdate,
    nestedComponent,
    nestedId
  };
};
